import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { DiagAutoType, FolderContents, WorkloadAssignees, WorkloadRole } from '../api/types';
import { useAuth } from '../contexts/AuthContext';
import { useLayoutContext } from '../components/Layout';
import { PermissionsModal } from '../components/PermissionsModal';
import { TrashModal } from '../components/TrashModal';
import { DIAGRAM_MOVED_EVENT, getDraggedDiagramId, isDiagramDrag, setDraggedDiagramId } from '../utils/dnd';
import { confirmLeaveIfPending } from '../utils/pendingSave';

const SYNTHESIS_TYPES: DiagAutoType[] = ['LOG', 'CHA', 'DES', 'FAC', 'COM'];

// Une carte de charge de travail par personne et par rôle, dans cet ordre d'affichage.
const WORKLOAD_ROLES: { role: WorkloadRole; list: keyof WorkloadAssignees; icon: string }[] = [
  { role: 'projectManager', list: 'projectManagers', icon: '🧑‍💼' },
  { role: 'drafter', list: 'drafters', icon: '✏️' },
  { role: 'siteManager', list: 'siteManagers', icon: '🏗️' },
  { role: 'techComponents', list: 'techComponents', icon: '🔧' },
];

export function FolderContentPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { folderId } = useParams();
  const navigate = useNavigate();
  const { tree, refreshTree } = useLayoutContext();

  const [contents, setContents] = useState<FolderContents | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [showNewDiagram, setShowNewDiagram] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [name, setName] = useState('');
  const [renameValue, setRenameValue] = useState('');
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
  const [workloadAssignees, setWorkloadAssignees] = useState<WorkloadAssignees | null>(null);

  const load = () => {
    if (!folderId) {
      setContents(null);
      return;
    }
    setError(null);
    api
      .get<FolderContents>(`/folders/${folderId}`)
      .then((c) => {
        setContents(c);
        setRenameValue(c.folder.name);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : String(err)));
    api.get<WorkloadAssignees>(`/folders/${folderId}/workload`).then(setWorkloadAssignees);
  };

  useEffect(load, [folderId]);

  // Un déplacement lancé depuis l'arborescence latérale (autre composant) peut
  // faire apparaître/disparaître un diagramme du dossier actuellement affiché.
  useEffect(() => {
    window.addEventListener(DIAGRAM_MOVED_EVENT, load);
    return () => window.removeEventListener(DIAGRAM_MOVED_EVENT, load);
  }, [folderId]);

  const guardClick = (e: React.MouseEvent) => {
    if (!confirmLeaveIfPending(t('common.leaveWithPendingSave'))) e.preventDefault();
  };

  const canWrite = folderId ? contents?.permissionLevel === 'WRITE' || contents?.permissionLevel === 'ADMIN' : user?.globalRole === 'ADMIN';
  const canAdmin = folderId ? contents?.permissionLevel === 'ADMIN' : user?.globalRole === 'ADMIN';

  const handleCreateFolder = async (e: FormEvent) => {
    e.preventDefault();
    await api.post('/folders', { name, parentId: folderId });
    setName('');
    setShowNewFolder(false);
    refreshTree();
    load();
  };

  const handleCreateDiagram = async (e: FormEvent) => {
    e.preventDefault();
    if (!folderId) return;
    const diagram = await api.post<{ id: string }>('/diagrams', { name, folderId });
    setName('');
    setShowNewDiagram(false);
    navigate(`/diagrams/${diagram.id}`);
  };

  const handleRename = async (e: FormEvent) => {
    e.preventDefault();
    if (!folderId) return;
    await api.patch(`/folders/${folderId}`, { name: renameValue });
    setShowRename(false);
    refreshTree();
    load();
  };

  const handleDeleteFolder = async () => {
    if (!folderId || !confirm(t('folders.deleteConfirm'))) return;
    try {
      await api.delete(`/folders/${folderId}`);
      refreshTree();
      navigate(contents?.folder.parentId ? `/folders/${contents.folder.parentId}` : '/');
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    }
  };

  const handleTrashDiagram = async (diagramId: string) => {
    await api.delete(`/diagrams/${diagramId}`);
    load();
  };

  const handleDuplicateDiagram = async (diagramId: string) => {
    await api.post(`/diagrams/${diagramId}/duplicate`);
    load();
  };

  const handleDropOnFolder = async (e: React.DragEvent, targetFolderId: string) => {
    e.preventDefault();
    setDragOverFolderId(null);
    const diagramId = getDraggedDiagramId(e);
    if (!diagramId) return;
    try {
      await api.patch(`/diagrams/${diagramId}/move`, { targetFolderId });
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    }
  };

  const rootNodes = tree;
  const subfolders = folderId
    ? contents?.children ?? []
    : rootNodes.map((n) => ({ id: n.id, name: n.name, parentId: n.parentId, permissionLevel: n.permissionLevel }));
  const diagrams = folderId ? contents?.diagrams ?? [] : [];

  return (
    <div>
      <div className="breadcrumb">
        <Link to="/">{t('folders.root')}</Link>
        {contents?.breadcrumb.map((b) => (
          <span key={b.id}>
            {' / '}
            <Link to={`/folders/${b.id}`}>{b.name}</Link>
          </span>
        ))}
        {contents && !showRename && (
          <span>
            {' / '}
            {contents.folder.name}
            {canAdmin && (
              <button className="btn" style={{ marginLeft: 8, padding: '2px 8px' }} onClick={() => setShowRename(true)}>
                {t('folders.rename')}
              </button>
            )}
          </span>
        )}
      </div>

      {error && <p className="error-text">{error}</p>}

      {showRename && (
        <form className="card" style={{ marginBottom: 16, maxWidth: 360 }} onSubmit={handleRename}>
          <div className="form-field">
            <label>{t('folders.newFolderName')}</label>
            <input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} required autoFocus />
          </div>
          <div className="toolbar">
            <button type="submit" className="btn btn-primary">
              {t('common.confirm')}
            </button>
            <button type="button" className="btn" onClick={() => setShowRename(false)}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      )}

      <div className="toolbar">
        {canWrite && (
          <button className="btn" onClick={() => setShowNewFolder((v) => !v)}>
            {t('folders.newFolder')}
          </button>
        )}
        {folderId && canWrite && (
          <button className="btn" onClick={() => setShowNewDiagram((v) => !v)}>
            {t('folders.newDiagram')}
          </button>
        )}
        {folderId && canAdmin && (
          <button className="btn" onClick={() => setShowPermissions(true)}>
            {t('folders.permissions')}
          </button>
        )}
        {folderId && canWrite && (
          <button className="btn" onClick={() => setShowTrash(true)}>
            {t('trash.button', { count: contents?.trashCount ?? 0 })}
          </button>
        )}
        {folderId && canAdmin && (
          <button className="btn btn-danger" onClick={handleDeleteFolder}>
            {t('folders.delete')}
          </button>
        )}
        {!folderId && user?.globalRole !== 'ADMIN' && rootNodes.length === 0 && (
          <span className="badge">{t('folders.createRootHint')}</span>
        )}
      </div>

      {showNewFolder && (
        <form className="card" style={{ marginBottom: 16 }} onSubmit={handleCreateFolder}>
          <div className="form-field">
            <label>{t('folders.newFolderName')}</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
          </div>
          <button type="submit" className="btn btn-primary">
            {t('common.confirm')}
          </button>
        </form>
      )}

      {showNewDiagram && (
        <form className="card" style={{ marginBottom: 16 }} onSubmit={handleCreateDiagram}>
          <div className="form-field">
            <label>{t('folders.newDiagramName')}</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
          </div>
          <button type="submit" className="btn btn-primary">
            {t('common.confirm')}
          </button>
        </form>
      )}

      {subfolders.length > 0 && (
        <>
          <h4>{t('folders.subfolders')}</h4>
          <div className="grid" style={{ marginBottom: 24 }}>
            {subfolders.map((f) => {
              const canDropHere = f.permissionLevel === 'WRITE' || f.permissionLevel === 'ADMIN';
              return (
                <Link
                  key={f.id}
                  to={`/folders/${f.id}`}
                  className={`item-card${dragOverFolderId === f.id ? ' drop-target' : ''}`}
                  onDragOver={(e) => {
                    if (!canDropHere || !isDiagramDrag(e)) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                  }}
                  onDragEnter={() => canDropHere && setDragOverFolderId(f.id)}
                  onDragLeave={() => setDragOverFolderId((cur) => (cur === f.id ? null : cur))}
                  onDrop={(e) => canDropHere && handleDropOnFolder(e, f.id)}
                >
                  📁 {f.name}
                </Link>
              );
            })}
          </div>
        </>
      )}

      {folderId && (
        <>
          <h4>{t('folders.diagrams')}</h4>
          {diagrams.length === 0 && subfolders.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)' }}>{t('folders.empty')}</p>
          ) : (
            <div className="grid">
              {diagrams.map((d) => (
                <div
                  key={d.id}
                  className="item-card"
                  style={{ cursor: canWrite ? 'grab' : 'default' }}
                  draggable={canWrite}
                  onDragStart={(e) => canWrite && setDraggedDiagramId(e, d.id)}
                >
                  <Link to={`/diagrams/${d.id}`} onClick={guardClick} style={{ color: 'inherit', textDecoration: 'none' }}>
                    📊 {d.name}
                    <br />
                    <span className="badge">{d.status}</span>
                  </Link>
                  {canWrite && (
                    <div className="toolbar" style={{ marginTop: 4 }}>
                      <button className="btn" style={{ padding: '2px 8px' }} onClick={() => handleDuplicateDiagram(d.id)}>
                        {t('diagram.duplicate')}
                      </button>
                      <button className="btn btn-danger" style={{ padding: '2px 8px' }} onClick={() => handleTrashDiagram(d.id)}>
                        {t('folders.delete')}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {folderId && (
        <>
          <h4>{t('synthesis.sectionTitle')}</h4>
          <div className="grid" style={{ marginBottom: 24 }}>
            {SYNTHESIS_TYPES.map((type) => (
              <Link
                key={type}
                to={`/folders/${folderId}/synthesis/${type}`}
                onClick={guardClick}
                className="item-card"
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                🧮 {type} — {t(`synthesis.labels.${type}`)}
              </Link>
            ))}
          </div>

          <h4>{t('synthesis.workloadSectionTitle')}</h4>
          {workloadAssignees && WORKLOAD_ROLES.every(({ list }) => workloadAssignees[list].length === 0) ? (
            <p style={{ color: 'var(--color-text-muted)' }}>{t('synthesis.workloadEmpty')}</p>
          ) : (
            <div className="grid" style={{ marginBottom: 24 }}>
              {WORKLOAD_ROLES.flatMap(({ role, list, icon }) =>
                (workloadAssignees?.[list] ?? []).map((u) => (
                  <Link
                    key={`${role}-${u.id}`}
                    to={`/folders/${folderId}/workload/${role}/${u.id}`}
                    onClick={guardClick}
                    className="item-card"
                    style={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    {icon} {u.firstName} {u.lastName} — {t(`synthesis.role.${role}`)}
                  </Link>
                )),
              )}
            </div>
          )}

          <h4>{t('master.sectionTitle')}</h4>
          <div className="grid" style={{ marginBottom: 24 }}>
            <Link
              to={`/folders/${folderId}/master`}
              onClick={guardClick}
              className="item-card"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              ⭐ {t('master.title')}
            </Link>
          </div>
        </>
      )}

      {!folderId && subfolders.length === 0 && <p style={{ color: 'var(--color-text-muted)' }}>{t('folders.noAccess')}</p>}

      {showPermissions && folderId && (
        <PermissionsModal folderId={folderId} onClose={() => setShowPermissions(false)} />
      )}

      {showTrash && folderId && (
        <TrashModal
          folderId={folderId}
          canPurge={canAdmin ?? false}
          onClose={() => setShowTrash(false)}
          onChanged={load}
        />
      )}
    </div>
  );
}
